import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { UserPlus } from 'lucide-react';
import type { PersonRef } from '@central/contracts';
import { Button } from '@/components/Button';
import { mensagemDeErro } from '@/lib/format';
import { NomeDePessoa, SeletorDePessoa, useDicionarioDePessoas } from '@/features/tickets/pessoas';
import { atribuirResponsavel, chaves } from '@/features/tickets/tickets.api';
import type { Chamado } from '@/features/tickets/types';

/**
 * Responsável pelo chamado.
 *
 * O campo de busca fica escondido até alguém pedir para trocar: quem abre o
 * detalhe quer **ler** de quem é o chamado, e um formulário aberto ao lado de
 * cada informação transforma a tela de leitura em painel de edição.
 *
 * A consulta do nome atual usa o mesmo dicionário do cabeçalho — a chave de
 * cache é por pessoa, então repetir o `useDicionarioDePessoas` aqui não custa
 * requisição nenhuma.
 */
export function ControleDeResponsavel({ chamado }: { chamado: Chamado }) {
  const clienteDeConsulta = useQueryClient();
  const pessoas = useDicionarioDePessoas([chamado.assigneeId]);
  const [trocando, setTrocando] = useState(false);

  const atribuir = useMutation<Chamado, Error, PersonRef>({
    mutationFn: (pessoa) => atribuirResponsavel(chamado.id, pessoa.id, chamado.version),
    onSuccess: (atualizado) => {
      setTrocando(false);
      // A atribuição muda a linha na lista e o cabeçalho do detalhe.
      void clienteDeConsulta.invalidateQueries({ queryKey: chaves.raiz });
      clienteDeConsulta.setQueryData(chaves.item(chamado.id), atualizado);
    },
  });

  return (
    <div className="space-y-2">
      <p className="text-sm font-medium">Responsável</p>

      <div className="flex flex-wrap items-center gap-2 text-sm">
        <NomeDePessoa id={chamado.assigneeId} pessoas={pessoas} semId="não atribuído" />
        <Button
          size="sm"
          variant="ghost"
          onClick={() => setTrocando((aberto) => !aberto)}
          icon={<UserPlus className="h-4 w-4" aria-hidden />}
        >
          {chamado.assigneeId ? 'Trocar' : 'Atribuir'}
        </Button>
      </div>

      {trocando ? (
        <SeletorDePessoa
          rotulo="Buscar responsável"
          desabilitado={atribuir.isPending}
          aoEscolher={(pessoa) => atribuir.mutate(pessoa)}
        />
      ) : null}

      {atribuir.isError ? (
        <p role="alert" className="text-sm text-destructive">
          {mensagemDeErro(atribuir.error)}
        </p>
      ) : null}
    </div>
  );
}
